import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TooltipPosition, TooltipTheme } from '@shared/enum/tooltip.enums';
import { FlightChangeRequestControllerService } from '@booking/angular-booking-reservation-service';
import { getBookingStatusKey, getBookingStatusStyle } from '@shared/utils/helper';
import { Subject, takeUntil } from 'rxjs';
import { CountryCodeService } from 'src/app/services/country-code.service';

@Component({
    selector: 'flight-details-popup',
    templateUrl: './flight-details-popup.component.html',
    styleUrls: ['./flight-details-popup.component.scss']
})
export class FlightDetailsPopupComponent {
    @Input() type: string;
    @Input() flightDetail: any;
    @Input() id: any = '';

    @Output() closePopupEmit: EventEmitter<any> = new EventEmitter();

    currentModeView = 'DEPART';
    currentSegments: any;
    currentSegmentType: string;
    isLoading: boolean = true;
    detailsData: any;
    itineraries: any[] = [];
    TooltipPosition: typeof TooltipPosition = TooltipPosition;
    TooltipTheme: typeof TooltipTheme = TooltipTheme;
    bookingStatus: string = '';
    bookingStatusStyle: string = '';
    passengers: any[] = [];
    availableItineraries: any[] = [];
    currentIndexTag: number = 0;
    bookingInfo: any;
    flightInfo: any;
    changeNameData: any[] = [];
    addOnsData: any[] = [];
    onDestroy$: Subject<void> = new Subject();

    constructor(
        private flightChangeRequestControllerService: FlightChangeRequestControllerService,
        private countryCodeService: CountryCodeService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['id'] && changes['id'].currentValue) {
            this.isLoading = true;
            this.getViewDetailsData();
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    getViewDetailsData() {
        this.flightChangeRequestControllerService
            .viewChildReservation1(this.id)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: (res: any) => {
                    if (res.success) {
                        // CANCEL
                        if (this.type === 'CANCEL_FLIGHT') {
                            this.detailsData = res.data.cancel_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            this.itineraries = this.flightDetail.flight_info.itineraries;
                            this.detailsData.canceled_list.forEach((item: any) => {
                                this.availableItineraries.push([item[0].itinerary_index]);
                            });
                            this.currentIndexTag = this.detailsData.canceled_list[0][0].itinerary_index;
                            this.passengers = this.detailsData.canceled_list[0];
                            this.bookingInfo = this.flightDetail.booking_info;
                            this.flightInfo = this.flightDetail.flight_info;
                        }
                        // FINDING FLIGHT
                        if (this.type === 'FINDING_FLIGHT') {
                            this.detailsData = res.data.finding_flight_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            // Set itineraries data
                            const source = this.detailsData.action !== 'REQUEST_REFUND'
                                ? this.detailsData.flight_info
                                : this.detailsData.itineraries_for_refund;
                            this.itineraries = (source || []).filter((item: any) => item);
                            // Set Flight info
                            this.flightInfo = {
                                source: null,
                                inbound_source: null
                            };
                            this.itineraries.forEach((item: any) => {
                                const mappedResource = this.mapResource(item?.resource);
                                if (item.segment_type == 'departure') {
                                    this.flightInfo.source = mappedResource
                                } else if (item.segment_type == 'arrival') {
                                    this.flightInfo.inbound_source = mappedResource
                                }
                                // Set round tag
                                this.availableItineraries.push(item.segment_type == 'departure' ? 0 : 1);
                            });
                            this.currentIndexTag = this.availableItineraries[0];
                            this.passengers = this.itineraries[0].passengers;

                            const [first, second] = this.itineraries;
                            this.bookingInfo = {
                                reference_id: first && first.segment_type === 'departure' ? first.pnr_code : null,
                                inbound_reference_id: first && first.segment_type !== 'departure' ? first.pnr_code : second?.pnr_code ?? null
                            };
                        }
                        // RESCHEDULE
                        if (this.type === 'RESCHEDULE_FLIGHT') {
                            this.detailsData = res.data.reschedule_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            // Set itineraries data
                            const source = this.detailsData.itineraries;
                            this.itineraries = (source || []).filter((item: any) => item);
                            // Set Flight info
                            this.flightInfo = {
                                source: null,
                                inbound_source: null
                            };
                            this.itineraries.forEach((item: any) => {
                                const mappedResource = this.mapResource(item?.resource);
                                
                                if (item.segment_type == 'departure') {
                                    this.flightInfo.source = mappedResource
                                } else if (item.segment_type == 'arrival') {
                                    this.flightInfo.inbound_source = mappedResource
                                }
                                // Set round tag
                                this.availableItineraries.push(item.segment_type == 'departure' ? 0 : 1);
                            });
                            this.currentIndexTag = this.availableItineraries[0];
                            this.passengers = this.itineraries[0].passengers;

                            const [first, second] = this.itineraries;
                            this.bookingInfo = {
                                reference_id: first && first.segment_type === 'departure' ? first.pnr_code : null,
                                inbound_reference_id: first && first.segment_type !== 'departure' ? first.pnr_code : second?.pnr_code ?? null
                            };
                        }
                        // CHANGE NAME
                        if (this.type === 'CHANGE_PASSENGER_NAME') {
                            this.changeNameData = [];
                            this.detailsData = res.data.change_name_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            this.detailsData.change_passenger_name_history.forEach((passenger: any) => {
                                const oldName = `${passenger.old_name.title}. ${passenger.old_name.last_name} ${passenger.old_name.middle_and_first_name}`;
                                const newName = `${passenger.new_name.title}. ${passenger.new_name.last_name} ${passenger.new_name.middle_and_first_name}`;
                                if (oldName != newName) {
                                    this.changeNameData.push(passenger);
                                }
                            });
                        }
                        // ADDITIONAL PURCHASING
                        if (this.type === 'ADDITIONAL_PURCHASING') {
                            this.addOnsData = [];
                            this.detailsData = res.data.add_on_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            this.detailsData = res.data.add_on_history[0];
                            this.detailsData.add_on_by_itinerary.forEach((itinerary: any) => {
                                this.addOnsData[itinerary.itinerary_index] = {
                                    itinerary_index: itinerary.itinerary_index,
                                    origin: itinerary.segment_iata_code_list[0][0],
                                    destination: itinerary.segment_iata_code_list[0][1],
                                    addonByPassengers: []
                                };
                                itinerary.add_ons.forEach((passenger: any) => {
                                    const passengerInfo = itinerary.passengers_info.find((p: any) => p.passenger_id === passenger.passenger_id);
                                    const formatData = this.groupAddOnsBySegment(passenger);
                                    this.addOnsData[itinerary.itinerary_index].addonByPassengers.push({
                                        passengerInfo,
                                        data: formatData
                                    });
                                });
                            });
                        }
                        // Load itinerary
                        if (this.type == 'CANCEL_FLIGHT' || this.type == 'RESCHEDULE_FLIGHT' || this.type == 'FINDING_FLIGHT') {
                            const itinerary = this.itineraries[0];
                            if (itinerary) {
                                this.currentSegments = itinerary.segments;
                                this.currentSegmentType = itinerary.segment_type;
                            }
                        }
                        // Load status and color booking status.
                        this.bookingStatus = getBookingStatusKey(res.data.reservation_status);
                        this.bookingStatusStyle = getBookingStatusStyle(res.data.reservation_status);
                    }
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                }
            });
    }

    mapResource(value: string): string {
        const map: { [key: string]: string } = {
            ag_hpl: 'HPL',
            ag_tbo: 'TBO',
            d_vietjet: 'VIETJET'
        };
        return map[value] || value;
    }

    handleClosePopup() {
        this.closePopupEmit.emit();
    }

    changeCurrentTag(currentIndex: number, index: number) {
        this.currentIndexTag = currentIndex;
        // Re init data when mode changes
        const itinerary = this.itineraries[index];
        if (itinerary) {
            this.currentSegments = itinerary.segments;
            this.currentSegmentType = itinerary.segment_type;
        }

        if (this.type === 'RESCHEDULE') {
            this.passengers = this.detailsData.itineraries[this.currentIndexTag].passengers;
        } else if (this.type === 'CANCEL') {
            this.passengers = this.detailsData.canceled_list[index];
        }
    }

    groupAddOnsBySegment(passenger: any) {
        const segmentsMap: Record<string, any> = {};

        const addSegment = (list: any[], type: 'seat' | 'meal' | 'baggage') => {
            if (!Array.isArray(list)) return;

            for (const item of list) {
                const key = `${item.origin_iata_code}-${item.destination_iata_code}`;
                if (!segmentsMap[key]) {
                    segmentsMap[key] = {
                        origin: item.origin_iata_code,
                        destination: item.destination_iata_code,
                        seat: null,
                        meals: null,
                        extra_baggage: null
                    };
                }

                if (type === 'seat') segmentsMap[key].seat = item;
                if (type === 'meal') segmentsMap[key].meals = item;
                if (type === 'baggage') segmentsMap[key].extra_baggage = item;
            }
        };

        addSegment(passenger.passenger_seat_list, 'seat');
        addSegment(passenger.extra_meal_list, 'meal');
        addSegment(passenger.extra_baggage_list, 'baggage');

        return Object.values(segmentsMap);
    }

    getUTCUser(): string {
        return this.countryCodeService.getUTCOffset();
    }
}
